import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { BASE_URL } from '../api/client';
import { ConsentState, updateGtag } from '../utils/gtag';

const STORAGE_KEY = 'ww_cookie_consent';
const SCHEMA_VERSION = 1;

interface StoredConsent extends ConsentState {
  version: number;
  timestamp: number;
}

interface ConsentContextValue {
  consent: ConsentState;
  isEU: boolean;
  showBanner: boolean;
  consentGiven: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  updateConsent: (c: ConsentState) => void;
}

const defaultConsent: ConsentState = {
  analytics: false,
  marketing: false,
  preferences: true,
};

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

function loadStored(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(c: ConsentState): void {
  const stored: StoredConsent = { ...c, version: SCHEMA_VERSION, timestamp: Date.now() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export const ConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const stored = loadStored();
  const [consent, setConsent] = useState<ConsentState>(stored ?? defaultConsent);
  const [isEU, setIsEU] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [consentGiven, setConsentGiven] = useState(stored !== null);

  useEffect(() => {
    if (stored !== null) {
      updateGtag(stored);
      return;
    }

    fetch(`${BASE_URL}/api/ip`)
      .then(r => r.json())
      .then((data: { is_eu: boolean }) => {
        setIsEU(data.is_eu);
        if (!data.is_eu) {
          const granted: ConsentState = { analytics: true, marketing: true, preferences: true };
          setConsent(granted);
          persist(granted);
          updateGtag(granted);
          setConsentGiven(true);
        } else {
          setShowBanner(true);
        }
      })
      .catch(() => {
        // Fail-safe: show banner
        setIsEU(true);
        setShowBanner(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyConsent = useCallback((c: ConsentState) => {
    setConsent(c);
    persist(c);
    updateGtag(c);
    setConsentGiven(true);
    setShowBanner(false);
  }, []);

  const acceptAll = useCallback(() => {
    applyConsent({ analytics: true, marketing: true, preferences: true });
  }, [applyConsent]);

  const rejectAll = useCallback(() => {
    applyConsent({ analytics: false, marketing: false, preferences: false });
  }, [applyConsent]);

  return (
    <ConsentContext.Provider value={{ consent, isEU, showBanner, consentGiven, acceptAll, rejectAll, updateConsent: applyConsent }}>
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider');
  return ctx;
};
